import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { DateTimeService } from '@y42/date-time';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  tap,
} from 'rxjs';
import { DatepickerRange, DateRangeConfig, Time } from '../types';

interface TimeRestriction {
  minTime: Time | null;
  maxTime: Time | null;
}

@UntilDestroy()
@Component({
  selector: 'y42-daterange-picker',
  templateUrl: './daterange-picker.component.html',
  styleUrls: ['./daterange-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DaterangePickerComponent {
  constructor(private readonly dateTimeService: DateTimeService) {
    const startTimeOnStartDateChange$ = this.buildTimeEmitterOnDateChange(this.startDate$, this.startTimeChanged$$);

    const endTimeOnEndDateChange$ = this.buildTimeEmitterOnDateChange(this.endDate$, this.endTimeChanged$$);

    const emitEventOnDatesChange$ = combineLatest([this.dateRange$, this.timeRange$]).pipe(
      map(([date, time]) => {
        const start = date.start ? new Date(date.start) : null;
        const end = date.end ? new Date(date.end) : null;
        if (start && time.start) {
          start.setHours(time.start.hour);
          start.setMinutes(time.start.minute);
        }
        if (end && time.end) {
          end.setHours(time.end.hour);
          end.setMinutes(time.end.minute);
        }
        return { start, end };
      }),
      distinctUntilChanged(
        (prev, next) => prev.start?.getTime() === next.start?.getTime() && prev.end?.getTime() === next.end?.getTime(),
      ),
      debounceTime(0),
      tap(({ start, end }) => {
        this.datesChanged.emit({ start, end });
      }),
    );

    merge(startTimeOnStartDateChange$, endTimeOnEndDateChange$, emitEventOnDatesChange$)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  @Input()
  @InputObservable()
  startDate: Date | null = null;
  startDate$!: Observable<Date | null>;

  @Input()
  @InputObservable()
  endDate: Date | null = null;
  endDate$!: Observable<Date | null>;

  @Input()
  @InputObservable()
  minDate: Date | null = null;
  minDate$!: Observable<Date | null>;

  @Input()
  @InputObservable()
  maxDate: Date | null = null;
  maxDate$!: Observable<Date | null>;

  @Input()
  singleDatePicker = false;

  @Input()
  timePicker = false;

  @Input()
  ranges: DateRangeConfig[] = [];

  @Output()
  datesChanged = new EventEmitter<DatepickerRange>();

  protected readonly nextMonth = this.dateTimeService.add(new Date(), { months: 1 });

  private readonly rangeSelected$$ = new Subject<DateRangeConfig>();
  private readonly dateChanged$$ = new Subject<DateRange<Date>>();
  private readonly startTimeChanged$$ = new Subject<Time | null>();
  private readonly endTimeChanged$$ = new Subject<Time | null>();

  private readonly dateRange$ = merge(
    this.dateChanged$$,
    this.rangeSelected$$.pipe(map(({ start, end }) => new DateRange(start, end))),
    combineLatest([this.startDate$, this.endDate$]).pipe(map(([start, end]) => new DateRange(start, end))),
  );

  private readonly timeRange$ = combineLatest([
    this.startTimeChanged$$.pipe(startWith(null)),
    this.endTimeChanged$$.pipe(startWith(null)),
  ]).pipe(map(([start, end]) => ({ start, end })));

  private readonly timeRestrictions$ = combineLatest([
    this.dateRange$,
    this.timeRange$,
    this.minDate$,
    this.maxDate$,
  ]).pipe(
    map(([dateRange, timeRange, minDate, maxDate]) => {
      const { start, end } = dateRange;
      let startTimeMaxTime: Time | null = null;
      if (!!end && (!start || this.dateTimeService.isSame(start, end, 'days'))) {
        startTimeMaxTime = timeRange.end;
      }
      if (!startTimeMaxTime && start && maxDate && this.dateTimeService.isSame(start, maxDate, 'days')) {
        startTimeMaxTime = { hour: maxDate.getHours(), minute: maxDate.getMinutes() };
      }

      const startTime: TimeRestriction = {
        minTime:
          !!start && minDate && this.dateTimeService.isSame(start, minDate, 'days')
            ? { hour: minDate.getHours(), minute: minDate.getMinutes() }
            : null,
        maxTime: startTimeMaxTime,
      };

      let endTimeMinTime: Time | null = null;
      if (!!start && (!end || this.dateTimeService.isSame(start, end, 'days'))) {
        endTimeMinTime = timeRange.start;
      }
      if (!endTimeMinTime && end && minDate && this.dateTimeService.isSame(end, minDate, 'days')) {
        endTimeMinTime = { hour: minDate.getHours(), minute: minDate.getMinutes() };
      }
      const endTime: TimeRestriction = {
        minTime: endTimeMinTime,
        maxTime:
          !!end && maxDate && this.dateTimeService.isSame(end, maxDate, 'days')
            ? { hour: maxDate.getHours(), minute: maxDate.getMinutes() }
            : null,
      };

      return { startTime, endTime };
    }),
  );

  private readonly startAt$ = this.dateRange$.pipe(
    map(({ start, end }) => {
      if (!start && !end) {
        return this.dateTimeService.add(new Date(), { months: 1 });
      }
      if (start && !end) {
        return this.dateTimeService.add(start, { months: 1 });
      }
      if (start && end && this.dateTimeService.isSame(start, end, 'months')) {
        return this.dateTimeService.add(start, { months: 1 });
      }
      return end;
    }),
  );

  readonly state = connectState(this, {
    dateRange: this.dateRange$,
    startTime: this.startTimeChanged$$,
    endTime: this.endTimeChanged$$,
    timeRestrictions: this.timeRestrictions$,
    startAt: this.startAt$,
    timezoneDiffInfo: of(null).pipe(
      map(() => {
        const isDifferentTimezone = this.dateTimeService.userToBrowserTimeZoneDiff.millisecondsDiff !== 0;

        const browserTimezone = this.dateTimeService.browserTimeZone;
        const userTimezone = this.dateTimeService.userTimeZone;

        return {
          isDifferentTimezone,
          tooltipInfo: `You've selected a timezone in your user profile (${userTimezone}) which is different than your browser's (${browserTimezone}). Please note that the selected date-time will be according to the browser's timezone (${browserTimezone}).`,
        };
      }),
    ),
  });

  selectRange(value: DateRangeConfig): void {
    this.rangeSelected$$.next(value);
  }

  changeDate(value: DateRange<Date>): void {
    this.dateChanged$$.next(value);
  }

  changeStartTime(value: Time | null): void {
    this.startTimeChanged$$.next(value);
  }

  changeEndTime(value: Time | null): void {
    this.endTimeChanged$$.next(value);
  }

  private buildTimeEmitterOnDateChange(date$: Observable<Date | null>, emitter: Subject<Time | null>) {
    return date$.pipe(
      tap((endDate) => {
        if (!endDate) {
          emitter.next(null);
          return;
        }
        const hour = endDate.getHours();
        const minute = endDate.getMinutes();
        emitter.next({ hour, minute });
      }),
    );
  }
}
